import React, { useCallback, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { PortalActivityRegistryItem } from "../api/contracts";
import {
  BooleanIndicator,
  PortalDataTable,
  PortalTableColumn,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { usePortalData } from "../hooks/usePortalData";

function EmptyValue() {
  return <span className="cp-empty-value">—</span>;
}

const columns: readonly PortalTableColumn<PortalActivityRegistryItem>[] = [
  {
    key: "activityKey",
    header: "Activity Key",
    initialWidth: 230,
    minWidth: 180,
    render: (activity) => (
      <code className="cp-registry-code">{activity.activityKey}</code>
    ),
  },
  {
    key: "label",
    header: "Label",
    initialWidth: 220,
    minWidth: 160,
    render: (activity) => activity.label || <EmptyValue />,
  },
  {
    key: "description",
    header: "Description",
    initialWidth: 340,
    minWidth: 220,
    render: (activity) => activity.description || <EmptyValue />,
  },
  {
    key: "buddyType",
    header: "Buddy Type",
    initialWidth: 150,
    minWidth: 120,
    render: (activity) => activity.buddyType || <EmptyValue />,
  },
  {
    key: "buddyOrder",
    header: "Buddy Order",
    initialWidth: 130,
    minWidth: 110,
    render: (activity) =>
      activity.buddyOrder === null ? <EmptyValue /> : activity.buddyOrder,
  },
  {
    key: "htmlFileName",
    header: "HTML File Name",
    initialWidth: 250,
    minWidth: 190,
    render: (activity) =>
      activity.htmlFileName ? (
        <code className="cp-registry-code">{activity.htmlFileName}</code>
      ) : (
        <EmptyValue />
      ),
  },
  {
    key: "enabled",
    header: "Enabled",
    initialWidth: 110,
    minWidth: 95,
    resizable: false,
    render: (activity) => (
      <BooleanIndicator
        value={activity.enabled}
        trueLabel="Enabled"
        falseLabel="Disabled"
      />
    ),
  },
];

export function ActivityRegistryPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [search, setSearch] = useState("");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getActivityRegistry(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null,
  );
  const activities = useMemo(() => data?.activities ?? [], [data?.activities]);
  const filteredActivities = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return activities;

    return activities.filter((activity) =>
      [
        activity.activityKey,
        activity.label,
        activity.description,
        activity.buddyType,
        activity.buddyOrder ?? "",
        activity.htmlFileName,
        activity.enabled ? "enabled" : "disabled",
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query),
    );
  }, [activities, search]);
  const resultCount = search.trim()
    ? `${filteredActivities.length} of ${activities.length} ${
        activities.length === 1 ? "entry" : "entries"
      }`
    : `${activities.length} ${activities.length === 1 ? "entry" : "entries"}`;

  return (
    <ClinicianPortalLayout
      activeGlobalKey="activity-registry"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Activity Registry"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Administration" },
              { label: "Activity Registry" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        {state.status === "loading" ? (
          <LoadingState label="Loading Activity Registry" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Activity Registry is unavailable")} />
        ) : null}
        {available && data ? (
          <section aria-label="Activity Registry list">
            <div className="cp-content-header">
              <div>
                <p>Review activities available to the homework assistant.</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>
            <div className="cp-list-toolbar">
              <div className="cp-list-toolbar__filters">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">
                    Search Activity Registry
                  </span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search Activity Registry"
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </label>
              </div>
            </div>
            {filteredActivities.length ? (
              <PortalDataTable
                caption="Activity Registry"
                columns={columns}
                records={filteredActivities}
                getRecordKey={(activity) => activity.id}
              />
            ) : activities.length ? (
              <EmptyState
                title="No Activity Registry entries match this search."
                description="Try a different activity detail."
              />
            ) : (
              <EmptyState
                title="No Activity Registry entries found."
                description="No activities are currently available in the registry."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
